/**
 * shared.ts — Tipos e rótulos do domínio de Viagens.
 *
 * Viagem não é só deslocamento de instalador: há a viagem das sócias e a
 * administrativa. Só a OPERACIONAL agrega OS e alimenta o custo do pedido.
 */
export type Transporte = 'carro' | 'onibus' | 'aviao' | 'outro';

export type TipoViagem = 'operacional' | 'socios' | 'administrativa';

export type StatusViagem =
  | 'planejada'
  | 'em_andamento'
  | 'concluida'
  | 'cancelada';

/** OS resumida (na lista de OS da viagem / OS disponíveis). */
export interface OSResumo {
  id: number;
  codigo: string;
  cliente_nome: string;
  cidade?: string | null;
}

export interface Instalador {
  id: number;
  nome: string;
}

/** Loja (empresa) — opção de seleção. */
export interface LojaOpcao {
  id: number;
  nome: string;
  codigo?: string | null;
}

/** Item da lista de viagens. */
export interface ViagemListItem {
  id: number;
  origem: string;
  nome_curto?: string | null;
  tipo: TipoViagem;
  loja_id?: number | null;
  loja_nome?: string | null;
  destinos: string[];
  data_partida?: string | null; // 'YYYY-MM-DD'
  data_chegada?: string | null; // 'YYYY-MM-DD'
  transporte: Transporte;
  status: StatusViagem;
  qtd_os: number;
  instaladores: string[];
  /**
   * Nulo para quem não tem `viagens.resumo.ver` — a API nem manda o número.
   * Ver a viagem e ver o custo dela são permissões diferentes desde 28/08/2026.
   */
  total_gasto?: number | null;
  encerrada_em?: string | null;
  /** O porquê do cancelamento (migration 132) — só na cancelada. */
  justificativa_cancelamento?: string | null;
}

/** Detalhe de uma viagem (form de edição e tela de consulta). */
export interface ViagemDetalhe {
  id: number;
  origem: string;
  /** Nome curto para a agenda. Vazio/nulo = a tela usa a sugestão (048). */
  nome_curto?: string | null;
  tipo: TipoViagem;
  loja_id?: number | null;
  loja_nome?: string | null;
  data_partida?: string | null;
  data_chegada?: string | null;
  transporte: Transporte;
  status: StatusViagem;
  observacoes?: string | null;
  destinos: string[];
  instaladores: Instalador[];
  os: OSResumo[];
  criado_em?: string | null;
  encerrada_em?: string | null;
  encerrada_por?: string | null;
  /** O cancelamento (migration 132): o porquê, quando e quem. */
  justificativa_cancelamento?: string | null;
  cancelada_em?: string | null;
  cancelada_por?: string | null;
}

/** Uma despesa lançada na viagem (tela de consulta). */
export interface DespesaDaViagem {
  id: number;
  data?: string | null;
  valor: number;
  descricao?: string | null;
  categoria?: string | null;
  lancado_por?: string | null;
  forma_pgto?: string | null;
  restituicao?: 'pendente' | 'restituido' | 'nao_aplicavel' | null;
  os_id?: number | null;
  os_codigo?: string | null;
  comprovante?: string | null;
}

export interface GastoPorOS {
  id: number;
  codigo: string;
  cliente_nome?: string | null;
  total_direto: number;
  peso: number;
}

/** Números da viagem (GET /viagens/:id/resumo). */
export interface ViagemResumo {
  total_gasto: number;
  qtd_despesas: number;
  dias?: number | null;
  custo_dia?: number | null;
  pendente_ressarcimento: number;
  /** Despesas da viagem sem OS. Ratear entre as OS é a Fatia 4. */
  nao_atribuido: number;
  por_categoria: { categoria: string; total: number }[];
  por_os: GastoPorOS[];
  despesas: DespesaDaViagem[];
}

/** Alias de domínio (forma canônica = detalhe). */
export type Viagem = ViagemDetalhe;

export const TRANSPORTES: {
  value: Transporte;
  label: string;
  icon: string;
}[] = [
  { value: 'carro', label: 'Carro', icon: 'bi-car-front' },
  { value: 'onibus', label: 'Ônibus', icon: 'bi-bus-front' },
  { value: 'aviao', label: 'Avião', icon: 'bi-airplane' },
  { value: 'outro', label: 'Outro', icon: 'bi-three-dots' },
];

export const TIPOS_VIAGEM: {
  value: TipoViagem;
  label: string;
  desc: string;
  icon: string;
}[] = [
  {
    value: 'operacional',
    label: 'Operacional',
    desc: 'Instaladores executando OS. Agrega OS e alimenta o custo do pedido.',
    icon: 'bi-tools',
  },
  {
    value: 'socios',
    label: 'Sócios',
    desc: 'Viagem das sócias. Despesas próprias, sem OS.',
    icon: 'bi-person-badge',
  },
  {
    value: 'administrativa',
    label: 'Administrativa',
    desc: 'Demais deslocamentos. Sem OS.',
    icon: 'bi-briefcase',
  },
];

export const rotuloTipoViagem = (t: TipoViagem): string =>
  TIPOS_VIAGEM.find((x) => x.value === t)?.label ?? t;

export const iconeTipoViagem = (t: TipoViagem): string =>
  TIPOS_VIAGEM.find((x) => x.value === t)?.icon ?? 'bi-geo-alt';

/** Só a viagem operacional agrega OS (e, na Fatia 4, alimenta o custo do pedido). */
export const agregaOS = (t: TipoViagem): boolean => t === 'operacional';

export const STATUS_VIAGEM: { value: StatusViagem; label: string }[] = [
  { value: 'planejada', label: 'Planejada' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
];

export const rotuloTransporte = (t: Transporte): string =>
  TRANSPORTES.find((x) => x.value === t)?.label ?? t;

export const iconeTransporte = (t: Transporte): string =>
  TRANSPORTES.find((x) => x.value === t)?.icon ?? 'bi-three-dots';

export const rotuloStatusViagem = (s: StatusViagem): string =>
  STATUS_VIAGEM.find((x) => x.value === s)?.label ?? s;

type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'destructive'
  | 'warning'
  | 'info';

export const varianteStatusViagem = (s: StatusViagem): BadgeVariant => {
  switch (s) {
    case 'planejada':
      return 'secondary';
    case 'em_andamento':
      return 'info';
    case 'concluida':
      return 'success';
    case 'cancelada':
      return 'destructive';
    default:
      return 'secondary';
  }
};

// Variantes semânticas do domínio -> classes de badge do Bootstrap.
export function badge(v: string): string {
  const mapa: Record<string, string> = {
    primary: 'text-bg-primary',
    info: 'text-bg-info',
    success: 'text-bg-success',
    warning: 'text-bg-warning',
    destructive: 'text-bg-danger',
    secondary: 'text-bg-secondary',
  };
  return mapa[v] ?? 'text-bg-secondary';
}
